import { useParams } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { useDowntime } from "@/contexts/DowntimeContext.jsx";
import AssetLayout from "./asset-layout.jsx";
import { StatCard } from "@/components/downtime-stat-card.jsx";
import { Clock, Wrench, AlertTriangle, Bell, WifiOff } from "lucide-react";
import { findAssetByRouteId } from "@/lib/asset-data-utils.js";
import {
  ASSET_KPI_SUBTITLE,
  getAssetKpiFormattedValues,
  useAssetKpiSummary,
} from "@/hooks/useAssetKpiSummary.js";
import { useAssetTagTrend } from "@/hooks/useAssetTagTrend.js";
import AssetTagTrend from "@/components/asset-tag-trend.jsx";

export default function AssetVitals() {
  const { id } = useParams();
  const { assets: compressors } = useAssets();
  const { downtimeEvents } = useDowntime();
  const compressor = findAssetByRouteId(compressors, id);
  const tagTrendData = useAssetTagTrend(compressor);
  const {
    kpiMetrics,
    communicationsAvailabilityDisplay,
    isLoadingComm,
    alarmsSubtitle,
  } = useAssetKpiSummary(compressor, {
    allParameters: tagTrendData.allParameters,
  });

  if (!compressor) return null;

  const formatted = getAssetKpiFormattedValues(
    kpiMetrics,
    communicationsAvailabilityDisplay,
    alarmsSubtitle
  );

  const statCards = [
    {
      title: "Runtime",
      value: formatted.runtime,
      icon: Clock,
      status: "all",
    },
    {
      title: "Mechanical Availability",
      value: formatted.mechanicalAvailability,
      icon: Wrench,
      status: "mechanical",
    },
    {
      title: "Communications Availability",
      value: formatted.communicationsAvailability,
      icon: WifiOff,
      status: "nodata",
      isLoading: isLoadingComm,
    },
    {
      title: "Downtime Events",
      value: formatted.downtimeEvents,
      icon: AlertTriangle,
      status: "events",
    },
    {
      title: "Active Alarms",
      value: formatted.activeAlarmsTotal,
      subtitle: formatted.alarmsSubtitle,
      icon: Bell,
      status: "danger",
    },
  ];

  return (
    <AssetLayout>
      <div className='w-full max-w-full space-y-6'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6'>
          {statCards.map(({ title, value, subtitle, icon, status, isLoading }) => (
            <StatCard
              key={title}
              title={title}
              value={value}
              subtitle={subtitle ?? ASSET_KPI_SUBTITLE}
              icon={icon}
              status={status}
              isLoading={isLoading}
            />
          ))}
        </div>

        <AssetTagTrend
          tagTrendData={tagTrendData}
          compressor={compressor}
          downtimeEvents={downtimeEvents}
          layout={{ type: "vitals", showTrendAlways: false }}
        />
      </div>
    </AssetLayout>
  );
}
